/**
 * User Details Modal Component
 * Displays user profile information, site permissions, and action controls.
 */

import { useState } from 'react';
import { UserProfile, UserSitePermission, coreAPI } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { departmentLabel } from '../../lib/departments';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { SetTempPasswordDialog } from './SetTempPasswordDialog';
import { PrimaryContactSection } from './PrimaryContactSection';
import { UserBuyerCodesField } from './UserBuyerCodesField';
import { UserDepartmentField } from './UserDepartmentField';
import { UserPlatformStaffField } from './UserPlatformStaffField';
import { UserDetailsStatusBar } from './UserDetailsStatusBar';
import { UserDetailsSiteAccessTab } from './UserDetailsSiteAccessTab';
import { canSendInvitation } from './userInvitation';
import { RoleBadges } from './RoleBadges';
import {
  Mail, Phone, Building2, UserCheck, Calendar, MapPin,
  User as UserIcon, Ban, UserCog, KeyRound, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

function Label({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={className}>{children}</div>;
}

interface UserDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedUser: UserProfile | null;
  userDetailTab: 'profile' | 'companies';
  setUserDetailTab: (tab: 'profile' | 'companies') => void;
  userSitePermissions: UserSitePermission[];
  loadingSitePermissions: boolean;
  savingPermission: number | null;
  savingDivision: number | null;
  /** True when the logged-in user is a Django superuser (may use Super admin switch). */
  isSuperAdmin: boolean;
  actionLoading: boolean;
  isSelf: (userProfile: UserProfile) => boolean;
  handleToggleActive: (userProfile: UserProfile) => void;
  openChangeRoleDialog: (userProfile: UserProfile) => void;
  /** Platform staff only: open the change-company (relink) dialog. */
  openChangeCompanyDialog?: (userProfile: UserProfile) => void;
  handleToggleAdmin: (userProfile: UserProfile, isAdmin: boolean) => void;
  handleToggleSiteLink: (siteId: number, isLinked: boolean) => Promise<void>;
  handleToggleSitePermission: (siteId: number, permission: string, value: boolean) => Promise<void>;
  handleAssignDivision: (
    groupId: number,
    opts: { isLinked: boolean; modules?: Record<string, boolean>; applyModulesToAll?: boolean }
  ) => Promise<void>;
  handleApproveUser: (userId: number) => Promise<void>;
  handleRejectUser: (userId: number) => Promise<void>;
  handleSendInvitation: (userProfile: UserProfile) => Promise<void>;
  /** CR-6273: true when the logged-in admin may impersonate (VC-internal or
   *  customer admin). Drives whether the "Login As" footer button is shown. */
  canImpersonate?: boolean;
  /** CR-6273: impersonate the given user (scoped to this User-management view). */
  handleLoginAs?: (userProfile: UserProfile) => void;
  /** SI-25: user id whose impersonation request is in flight. The footer button
   *  disables and spins on it, as the caller's row action already does — the
   *  server rotates the admin's own token per call, so a second click destroys
   *  the original_token the first one returned. */
  impersonatingUserId?: number | null;
  /** Refresh the user list after primary-contact designations change, so the
   *  row (and the Supplier Detail badge) reflect the new assignment. */
  onPrimaryContactsSaved?: () => void;
  /** Merge a saved department into the user list row (customer users only). */
  onDepartmentSaved?: (profileId: number, department: string) => void;
}

export function UserDetailsModal({
  open,
  onOpenChange,
  selectedUser,
  userDetailTab,
  setUserDetailTab,
  userSitePermissions,
  loadingSitePermissions,
  savingPermission,
  savingDivision,
  isSuperAdmin,
  actionLoading,
  isSelf,
  handleToggleActive,
  openChangeRoleDialog,
  openChangeCompanyDialog,
  handleToggleAdmin,
  handleToggleSiteLink,
  handleToggleSitePermission,
  handleAssignDivision,
  handleApproveUser,
  handleRejectUser,
  handleSendInvitation,
  canImpersonate = false,
  handleLoginAs,
  impersonatingUserId = null,
  onPrimaryContactsSaved,
  onDepartmentSaved,
}: UserDetailsModalProps) {
  const { formatDateTime } = useFormatters();

  // Temporary password. Reaching this modal already means the viewer passed
  // CanManageUsers, and the backend re-checks it per target (own company under
  // the admin-tier rule, or a supplier they trade with) and refuses staff
  // accounts — so customer admins get it too, for invitations that never
  // arrive and therefore can't be recovered by the reset email either.
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [settingPassword, setSettingPassword] = useState(false);

  /**
   * Persists the chosen password.
   *
   * @returns true when it was accepted, so the dialog stays open on a rejected
   *   password and the value typed is not lost.
   */
  const setTempPasswordOnUser = async (password: string): Promise<boolean> => {
    if (!selectedUser || !password) return false;
    setSettingPassword(true);
    try {
      await coreAPI.setTempUserPassword(selectedUser.id, password);
      toast.success('Temporary password set — share it with the user securely');
      return true;
    } catch (err: unknown) {
      console.error('Failed to set temp password:', err);
      const detail = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(detail || 'Failed to set temporary password');
      return false;
    } finally {
      setSettingPassword(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(openVal) => {
      onOpenChange(openVal);
      if (!openVal) setUserDetailTab('profile');
    }}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserIcon className="h-5 w-5" />
            User Details
          </DialogTitle>
          <DialogDescription>
            {selectedUser?.user.full_name} • {selectedUser?.user.email}
          </DialogDescription>
        </DialogHeader>

        {selectedUser && (
          <Tabs value={userDetailTab} onValueChange={(v) => setUserDetailTab(v as 'profile' | 'companies')} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="profile" className="gap-2">
                <UserIcon className="h-4 w-4" />
                Profile
              </TabsTrigger>
              <TabsTrigger value="companies" className="gap-2">
                <Building2 className="h-4 w-4" />
                Related Companies
              </TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile" className="flex-1 overflow-y-auto mt-4 space-y-6">
              {/* Status & Actions Bar */}
              <UserDetailsStatusBar
                selectedUser={selectedUser}
                isSuperAdmin={isSuperAdmin}
                actionLoading={actionLoading}
                isSelf={isSelf}
                handleToggleActive={handleToggleActive}
                openChangeRoleDialog={openChangeRoleDialog}
                openChangeCompanyDialog={openChangeCompanyDialog}
                handleToggleAdmin={handleToggleAdmin}
              />

              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Full Name</Label>
                  <p className="text-base mt-1">{selectedUser.user.full_name}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Email</Label>
                  <p className="text-base mt-1 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    {selectedUser.user.email}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Company</Label>
                  <p className="text-base mt-1 flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    {selectedUser.company_name}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">User Role</Label>
                  {/* div, not p: Badge renders a div, and a div inside a p is
                      invalid HTML that React reports as a hydration error. */}
                  <div className="text-base mt-1">
                    <RoleBadges userProfile={selectedUser} className="" />
                  </div>
                </div>
              </div>

              {/* Buyer codes (customer material controllers): drives the
                  "my suppliers" default scoping on purchasing list views. */}
              {selectedUser.company_type === 'customer' && (
                <UserBuyerCodesField selectedUser={selectedUser} />
              )}

              {/* Set a temporary password on this managed customer/supplier
                  user (no email round-trip). Helix-tenant targets stay excluded
                  here, and the backend refuses staff accounts outright. */}
              {selectedUser.company_type !== 'helix' && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Temporary Password</Label>
                  <div className="mt-1">
                    <Button size="sm" variant="outline" onClick={() => setPasswordDialogOpen(true)}>
                      <KeyRound className="h-4 w-4 mr-2" />
                      Set temporary password
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Applies immediately — share it with the user securely. They can change it in My Account.
                  </p>
                </div>
              )}

              {/* Platform staff — super admins only; renders nothing otherwise. */}
              <UserPlatformStaffField selectedUser={selectedUser} />

              {/* Additional Info */}
              <div className="grid grid-cols-2 gap-4">
                {selectedUser.job_title && (
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Position</Label>
                    <p className="text-base mt-1">{selectedUser.job_title}</p>
                  </div>
                )}
                {/* Customer-company users: editable here. Supplier users keep
                    the read-only display; theirs is edited from the supplier's
                    Users tab. */}
                {selectedUser.company_type === 'customer' ? (
                  <UserDepartmentField selectedUser={selectedUser} onSaved={onDepartmentSaved} />
                ) : selectedUser.department && (
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Department</Label>
                    <p className="text-base mt-1">{departmentLabel(selectedUser.department)}</p>
                  </div>
                )}
                {selectedUser.phone && (
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Telephone</Label>
                    <p className="text-base mt-1 flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {selectedUser.phone}
                    </p>
                  </div>
                )}
              </div>

              {/* Sites Summary */}
              {selectedUser.site_names.length > 0 && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Current Company Access</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedUser.site_names.map((site, idx) => (
                      <Badge key={idx} variant="secondary" className="gap-1">
                        <MapPin className="h-3 w-3" />
                        {site}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Primary contact designations — supplier and/or customer
                  companies this user fronts. Helix companies are excluded
                  server-side, so hide the editor for internal accounts. */}
              {selectedUser.company_type !== 'helix' && (
                <div className="pt-4 border-t">
                  <PrimaryContactSection
                    selectedUser={selectedUser}
                    canManage={Boolean(selectedUser.can_manage_user)}
                    onSaved={onPrimaryContactsSaved}
                  />
                </div>
              )}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Last login</Label>
                  <p className="text-base mt-1 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {selectedUser.user.last_login
                      ? formatDateTime(selectedUser.user.last_login)
                      : 'Never'}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Created At</Label>
                  <p className="text-base mt-1 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {formatDateTime(selectedUser.created_at)}
                  </p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="companies" className="flex-1 overflow-y-auto mt-4">
              <UserDetailsSiteAccessTab
                userSitePermissions={userSitePermissions}
                loadingSitePermissions={loadingSitePermissions}
                savingPermission={savingPermission}
                savingDivision={savingDivision}
                selectedUser={selectedUser}
                isSelf={isSelf}
                handleToggleSiteLink={handleToggleSiteLink}
                handleToggleSitePermission={handleToggleSitePermission}
                handleAssignDivision={handleAssignDivision}
              />
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter className="border-t pt-4 flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {/* CR-6273: Login As lives only here, in the user-detail view, so it
              is always scoped to the user the admin is inspecting. Hidden for
              self, inactive users, and Helix platform users (also blocked
              server-side — a Helix account must never be impersonated). */}
          {canImpersonate && handleLoginAs && selectedUser
            && !isSelf(selectedUser)
            && selectedUser.company_type !== 'helix'
            && selectedUser.user.is_active && (
            <Button
              variant="outline"
              disabled={actionLoading || impersonatingUserId === selectedUser.user.id}
              onClick={() => handleLoginAs(selectedUser)}
            >
              {impersonatingUserId === selectedUser.user.id ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <UserCog className="h-4 w-4 mr-2" />
              )}
              Login As
            </Button>
          )}
          {/* CR-5314: hide the invitation button for any user shown as
              "Active" (joined, or active and never invited). It stays visible
              only for non-active users — "Invite Sent" (resend) and inactive
              not-yet-joined accounts. See canSendInvitation for the rule. */}
          {selectedUser && !isSelf(selectedUser) && canSendInvitation(selectedUser.user) && (
            <Button
              variant="outline"
              disabled={actionLoading}
              onClick={async () => {
                await handleSendInvitation(selectedUser);
              }}
            >
              <Mail className="h-4 w-4 mr-2" />
              Send invitation email
            </Button>
          )}
          {selectedUser && !selectedUser.is_approved && !isSelf(selectedUser) && (
            <>
              <Button
                variant="destructive"
                disabled={actionLoading}
                onClick={async () => {
                  await handleRejectUser(selectedUser.id);
                }}
              >
                <Ban className="h-4 w-4 mr-2" />
                Reject
              </Button>
              <Button
                disabled={actionLoading}
                onClick={async () => {
                  await handleApproveUser(selectedUser.id);
                  onOpenChange(false);
                }}
              >
                <UserCheck className="h-4 w-4 mr-2" />
                Approve user
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Sibling of the details dialog, not a child: nesting it inside the
        Radix root left it unable to open. */}
    {selectedUser && (
      <SetTempPasswordDialog
        open={passwordDialogOpen}
        onOpenChange={setPasswordDialogOpen}
        userName={selectedUser.user.full_name || selectedUser.user.email}
        saving={settingPassword}
        onSubmit={setTempPasswordOnUser}
      />
    )}
    </>
  );
}
